<template>
  <transition
    enter-active-class="transition ease-out duration-300"
    enter-from-class="transform -translate-y-full opacity-0"
    enter-to-class="transform translate-y-0 opacity-100"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="transform translate-y-0 opacity-100"
    leave-to-class="transform -translate-y-full opacity-0"
  >
    <div
      v-if="showBanner"
      class="fixed top-0 left-0 right-0 z-50"
    >
      <!-- Offline Banner -->
      <div
        v-if="!offlineStore.isOnline"
        class="bg-amber-500 text-white px-4 py-3 shadow-lg"
      >
        <div class="max-w-7xl mx-auto flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p class="font-medium">You're offline</p>
              <p class="text-sm text-amber-100">
                Your selections are saved and will be submitted automatically when connection is restored.
              </p>
            </div>
          </div>
          <button
            @click="checkConnection"
            :disabled="offlineStore.isChecking"
            class="flex-shrink-0 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <span v-if="offlineStore.isChecking">Checking...</span>
            <span v-else>Retry</span>
          </button>
        </div>
      </div>
      
      <!-- Queued Votes Banner -->
      <div
        v-else-if="offlineStore.hasQueuedVotes"
        class="bg-primary-600 text-white px-4 py-3 shadow-lg"
      >
        <div class="max-w-7xl mx-auto flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <svg class="w-5 h-5 flex-shrink-0 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <div>
              <p class="font-medium">
                {{ offlineStore.queueLength }} vote(s) queued
              </p>
              <p class="text-sm text-primary-100">
                Submitting your votes automatically...
              </p>
            </div>
          </div>
          <button
            @click="processQueue"
            :disabled="isProcessing"
            class="flex-shrink-0 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <span v-if="isProcessing">Submitting...</span>
            <span v-else>Submit Now</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useOfflineStore } from '@/stores/offlineStore'
import api from '@/services/api'
import { useToast } from 'vue-toastification'

const offlineStore = useOfflineStore()
const toast = useToast()

const isProcessing = ref(false)

const showBanner = computed(() => {
  return !offlineStore.isOnline || offlineStore.hasQueuedVotes
})

const checkConnection = async () => {
  const isOnline = await offlineStore.checkConnectivity()
  
  if (isOnline) {
    toast.success('Connection restored!')
    // Process any queued votes
    processQueue()
  } else {
    toast.error('Still offline. Please check your connection.')
  }
}

const processQueue = async () => {
  if (isProcessing.value) return
  
  isProcessing.value = true
  
  try {
    const result = await offlineStore.processQueue(api)
    
    if (result.processed > 0) {
      toast.success(`${result.processed} vote(s) submitted successfully!`)
    }
    
    if (result.failed > 0) {
      toast.warning(`${result.failed} vote(s) failed to submit.`)
    }
  } catch (error) {
    toast.error('Failed to process queued votes.')
  } finally {
    isProcessing.value = false
  }
}
</script>
